// 3rd party libs
import React, { createRef, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useOnClickOutside } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import cs from 'classnames';
import { Tooltip } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';

// local imports
import { selectedSharedWithMeFolder, setClickedFolderName, switchSharedWithMeModal } from '../../../store/folders/foldersSlice';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { bytesToGB, bytesToMB, useDimensions } from '../../../helpers';
import { Portal } from '../../../helpers/bodyPortal';
import { useOverflowAwareActionMenu } from '../../../helpers/useOverflowAwareActionMenu';
import { useAppDispatch } from '../../../store/hooks';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import { CONTENTS_SHARED_WITH_ME } from '../../../enums/TYPES';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import { MoreIcon } from '../../../assets/icons/threedots';
import { postFolderViewRequest } from '../../../api';
import styles from './SharedContent.module.scss';
import { RolesLowerCase } from '../../../enums';
import { PATHS } from '../../../enums/PATHS';
import ShareButton from '../ShareButton';
import { convertSeconds } from '../../Timeline/utils';
import { SortOptions } from '../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../DateSortHeader';

type TSharedFolder = {
  data: CONTENTS_SHARED_WITH_ME;
  search: string;
  limit: number;
  page: number;
  sort: string;
  setModalType: (v: string) => void;
  fetchType: 'folder' | 'composer' | 'all' | null;
  onOpen?: () => void;
  onShare?: () => void;
  layout?: 'root' | 'nested';
  pinContextType?: string;
  pinContextParentId?: string | number | null;
  onPinUpdated?: () => void;
  dateSortOption?: SortOptions | null;
};

export const SharedFolder: React.FC<TSharedFolder> = ({
  data,
  page,
  limit,
  sort,
  search,
  setModalType,
  fetchType,
  onOpen,
  onShare,
  layout = 'root',
  pinContextType,
  pinContextParentId,
  onPinUpdated,
  dateSortOption,
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const redirectRef = createRef<HTMLDivElement>();
  const notification = useSnackbar();

  const { name, owner, updated_at, created_at, access, id } = data;
  const isPinned = Boolean((data as any)?.is_pinned);
  const role = useMemo(() => {
    if (owner?.role?.title && owner?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN) {
      return 'OA';
    } else if (owner?.role?.title) {
      let role = owner.role.title;
      if (owner?.organization_users) {
        // @ts-ignore
        if (owner?.organization_users?.role?.title) {
          // @ts-ignore
          role = owner.organization_users.role.title;
        }
      }
      return role;
    } else {
      return '';
    }
  }, [owner]);

  const onShareClick = () => {
    setShowDotsMenu(false);
    if (onShare) {
      onShare();
      return;
    }
    console.log(data);
    if (data.owner) {
      setModalType('folder');
      dispatch(
        selectedSharedWithMeFolder({
          ...data,
          id,
          // @ts-ignore
          access,
          created_at,
          name,
          owner,
          updated_at,
          videofeeds: [''],
          type: 'folder',
        }),
      );
      dispatch(switchSharedWithMeModal(true));
    } else {
      notification.showMessage(`Folder ${data.name} has no owner. Folder is disabled for usage for now`);
    }
  };

  const onPinToggle = async () => {
    try {
      if (isPinned) {
        const { unpinContent } = await import('../../../api/pinnedContentApi');
        await unpinContent({
          targetType: 'folder',
          targetId: id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      } else {
        const { pinContent } = await import('../../../api/pinnedContentApi');
        await pinContent({
          targetType: 'folder',
          targetId: id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      }
      onPinUpdated?.();
      notification.showMessage(isPinned ? 'Unpinned' : 'Pinned');
      setShowDotsMenu(false);
    } catch (err) {
      notification.showMessage('Failed to update pin');
    }
  };

  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({
    showMenu: showDotsMenu,
    anchorPosition: menuAnchorPosition,
  });

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }

    if (e.type === 'contextmenu') {
      setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    } else {
      setMenuAnchorPosition(null);
    }
    setShowDotsMenu(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    setMenuAnchorPosition(null);
  });

  const redirectToContent = async (e: React.MouseEvent<HTMLElement>) => {
    if (data.owner) {
      if (e.target === e.currentTarget) {
        dispatch(setClickedFolderName(name));
        postFolderViewRequest(data.id);
        if (onOpen) {
          onOpen();
          return;
        }
        history.push(
          `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${data.id}&parentPage=${PATHS.SHARED_WITH_ME.label}&page=${page}&limit=${limit}&sort=${sort}&search=${search}`,
        );
      }
    } else {
      notification.showMessage(`Folder ${data.name} has no owner. Folder is disabled for usage for now`);
    }
  };

  const { widthD } = useDimensions();

  return (
    <div
      className={cs(styles.container, layout === 'nested' && styles.nestedLayout, showDotsMenu && styles.selectedFolder)}
      id={id}
      onClick={redirectToContent}
      ref={redirectRef}
      onContextMenu={switchDotsMenu}
      style={{
        position: 'relative',
        boxShadow: isPinned ? 'inset 0 0 0 1px rgba(57, 179, 234, 0.35)' : undefined,
      }}
    >
      <div className={styles.leftSide} onClick={redirectToContent}>
        <img className={cs(styles.fldrIcon, data.thumbnail_url && styles.customThumbnail)} src={data.thumbnail_url || FolderIcon} style={{ cursor: 'pointer' }} alt="folder icon" onClick={redirectToContent} />
        <div className={styles.name} onClick={redirectToContent}>
          {name}
        </div>
      </div>
      <div className={styles.owner}>
        <Tooltip title={owner?.username}>
          <span>{owner?.username}</span>
        </Tooltip>
      </div>
      <div className={styles.access}>
        {data?.highest_user_access?.access?.title !== (ACCESS_TYPE_ENUM.WITHOUT_ACCESS || ACCESS_TYPE_ENUM.NO_ACCESS)
          ? ACCESS_TYPE?.[data?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM]
          : ''}
      </div>
      <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(data, dateSortOption), true)}`}>
        <div className={styles.date}>
          {widthD > 1300
            ? DateFormatter(getDateColumnValue(data, dateSortOption), true)
            : DateFormatter(getDateColumnValue(data, dateSortOption))}
        </div>
      </Tooltip>

      <Tooltip title={data?.duration && `Upload Hours: ${convertSeconds(data.duration, false, true)}`}>
        <div className={styles.size}>
          <img src={StorageIcon} alt="storage" />
          {bytesToGB(Number(data?.size)) > 1
            ? `${bytesToGB(Number(data?.size)).toFixed(2)} GB`
            : `${bytesToMB(Number(data?.size)).toFixed(0)} MB`}
        </div>
      </Tooltip>

      <div className={styles.dotsMenuBtn}>
        <div ref={triggerRef} className={styles.dotIcon} onClick={switchDotsMenu}>
          <MoreIcon />
        </div>
      </div>

      <Portal>
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            cursor: 'context-menu',
            display: showDotsMenu ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
            right: 'auto',
          }}
        >
          <div
            className={cs(
              styles.menuOption,
              (data?.highest_user_access &&
                data?.highest_user_access.access.title !== ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE &&
                data?.highest_user_access.access.title !== ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE) ||
                !data?.highest_user_access
                ? styles.disabled
                : '',
            )}
            onClick={onShareClick}
          >
            <ShareButton /> Share
          </div>
          {pinContextType && (
            <div className={styles.menuOption} onClick={onPinToggle}>
              <span
                style={{
                  display: 'inline-flex',
                  width: 32,
                  height: 30,
                  padding: 4,
                  borderRadius: 'var(--ui-border-radius)',
                  background: 'var(--action-menu-icon-bg)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 12,
                  boxSizing: 'border-box',
                }}
              >
                <PushPinIcon sx={{ fontSize: 20, color: '#fff' }} />
              </span>
              {isPinned ? 'Unpin' : 'Pin'}
            </div>
          )}
        </div>
      </Portal>
    </div>
  );
};
